import { apiSocket } from '../apiSocket';

export type IAnInterval = 'day' | 'week' | 'month' | 'trimester';

export type IImageQuality = 'All' | '1K' | '2K' | '4K' | '8K';

export type IVideoFrames = 'All' | 25 | 100 | 200 | 300;

const VIDEO_PREVIOUS_FRAME = {
    25: 0,
    100: 25,
    200: 100,
    300: 200
};

const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: undefined
};
const SIMPLE_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
    month: 'numeric',
    day: 'numeric',
    hour: undefined
};

export interface IAnalytic {
    user_id: string;
    duration: number;
    settings: Record<string, unknown>;
    type: 'video' | 'image';
    created_at?: Date;
    _id?: string;
    project?: {
        name: string;
        id: string;
    }
}

export type IAnIntervalData = {
    name: string;
    simple: string;
    nb: number;
}[];

export type IAnalyticsResponse = {
    imagesData: IAnIntervalData;
    videoData: IAnIntervalData;
    viewsData: IAnIntervalData;
}

export const getAnalytics = (interval: IAnInterval, id: string, callback): void => { apiSocket.getSocket().emit('analytics:user', { interval, id }, callback); };
export const getUserInfos = (id: string, callback): void => { apiSocket.getSocket().emit('analytics:infos', { id }, callback); };

export function getCleanDate(d: string | Date): string {
    const date = new Date(d);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const hour = date.getHours();
    const mins = date.getMinutes();
    return `${day}/${month} - ${hour}:${mins}`;
}

function getLastWeek() {
    const today = new Date();
    const lastWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
    return lastWeek;
}

const checkDailyData = (x: IAnalytic,
    quality: IImageQuality, frames?: IVideoFrames) => {
    let qualityCheck = true;
    let framesCheck = true;
    if (quality && quality !== 'All') {
        qualityCheck = x.settings.resolution === quality;
    }
    if (frames && frames !== 'All') {
        const fTotal = Number(x.settings.fps) + Number(x.settings.fps);
        const previousFrame = VIDEO_PREVIOUS_FRAME[frames];
        framesCheck = fTotal < frames && fTotal > previousFrame;
    }
    return qualityCheck && framesCheck;
};

const getWeeklyArray = (data: IAnalytic[], quality: IImageQuality, frames?: IVideoFrames) => {
    const result = [];
    let currentDate = getLastWeek();
    let currentIndex = 0;
    const today = new Date();

    while (currentDate <= today) {
        const name = currentDate.toLocaleString('en-US', DATE_OPTIONS);
        const simple = currentDate.toLocaleString('en-US', SIMPLE_DATE_OPTIONS);
        if (currentIndex < data.length && new Date(data[currentIndex].created_at).toDateString()
            === currentDate.toDateString()) {
            // eslint-disable-next-line no-loop-func
            const item = result.find((x) => x.date === currentDate);
            const checkDaily = checkDailyData(data[currentIndex], quality, frames);
            if (checkDaily) {
                if (item) {
                    item.items.push(data[currentIndex]);
                    item.nb += 1;
                } else {
                    result.push({
                        date: currentDate, name, simple, items: [data[currentIndex]], nb: 1
                    });
                }
            }
            currentIndex += 1;
        } else {
            // eslint-disable-next-line no-loop-func
            const idx = result.findIndex((x) => x.date === currentDate);
            if (idx === -1) {
                result.push({
                    date: currentDate, name, simple, items: [], nb: 0
                });
            }
            currentDate = new Date(currentDate);
            currentDate.setDate(currentDate.getDate() + 1);
        }
    }
    return result;
};

function getWeekNumber(date) {
    const startOfWeek = new Date(date.getFullYear(), 0, 1);
    // @ts-ignore
    const diff = (date - startOfWeek) + ((startOfWeek.getDay() - 1) * 24 * 60 * 60 * 1000);
    const oneWeek = 7 * 24 * 60 * 60 * 1000;
    return Math.floor(diff / oneWeek);
}

const checkWeeklyData = (x: IAnalytic, weekNumber: number,
    quality: IImageQuality, frames?: IVideoFrames) => {
    const weekCheck = getWeekNumber(new Date(x.created_at)) === weekNumber;
    let qualityCheck = true;
    let framesCheck = true;
    if (quality && quality !== 'All') {
        qualityCheck = x.settings.resolution === quality;
    }
    if (frames && frames !== 'All') {
        const fTotal = Number(x.settings.fps) + Number(x.settings.fps);
        const previousFrame = VIDEO_PREVIOUS_FRAME[frames];
        framesCheck = fTotal < frames && fTotal > previousFrame;
    }
    return weekCheck && qualityCheck && framesCheck;
};

const getMonthlyArray = (data: IAnalytic[], quality: IImageQuality, frames?: IVideoFrames) => {
    const result = [];
    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(today.getDate() - 31);

    while (today >= endDate) {
        const name = today.toLocaleString('en-US', DATE_OPTIONS);
        const simple = today.toLocaleString('en-US', SIMPLE_DATE_OPTIONS);
        const weekNumber = getWeekNumber(today);
        const weekData = data.filter((x) => checkWeeklyData(x, weekNumber, quality, frames));
        result.unshift({
            name, simple, nb: weekData.length
        });
        today.setDate(today.getDate() - 7);
    }
    return result;
};

export const getAnalyticArray = (int: IAnInterval, data: IAnalytic[],
    quality: IImageQuality, frames?: IVideoFrames): IAnIntervalData => {
    switch (int) {
        case 'week':
            return getWeeklyArray(data, quality, frames);
        case 'month':
            return getMonthlyArray(data, quality, frames);
        default:
            return getWeeklyArray(data, quality, frames);
    }
};
